// Client for the MCP authorization front's sign-in endpoints (/oauth/*), used only by the /connect
// page. Like auth-client it keeps nothing in storage: the transaction is bound to the browser by
// an HttpOnly cookie the backend sets, and the page carries only the opaque `tx` from the URL.
// Request and response shapes are fixed by the contract mirrored in
// unit-tests/fixtures/oauth-contract.ts.

import {
  AuthServiceError,
  ChallengeExpiredError,
  InvalidCredentialsError,
  InvalidPasswordError,
  NotAuthorizedError,
  TooManyAttemptsError
} from './auth-errors';
import {
  CONNECT_PATH,
  ConnectError,
  ConnectStep,
  ConnectStepType,
  ConsentInfo,
  ConsentResult,
  OAUTH_POST_HEADERS,
  OAuthRouteMethod,
  OAuthRouteResult,
  OAuthSignInResult,
  OAuthSignInStatus
} from './oauth-constants';

/** The page that issued the transaction is gone: the 10 minute window passed or the cookie is missing. */
export class TxExpiredError extends Error {
  constructor(
    message = 'This sign-in expired. Please start again from the app you are connecting.'
  ) {
    super(message);
  }
}

// Hosts that serve the front's endpoints. Every sign-in call is same-origin, so the page works on
// whichever of these it is loaded from.
export const ISSUER_HOSTS = ['login.ai.tinuiti.com', 'experimental.blisspointmedia.com'] as const;

// Issuer hosts that exist only for sign-in. Any other path there is a mistake (or an attempt to
// borrow the host's name), so it goes to the app. The pre-prod issuer is also the pre-prod app and
// keeps serving it.
const DEDICATED_ISSUER_HOSTS: readonly string[] = ['login.ai.tinuiti.com'];

export const APP_URL = 'https://blisspoint.tinuiti.com';

export const ConnectRouteMode = {
  /** The consent page: no app session, no boot refresh, no destination capture. */
  CONNECT: 'connect',
  /** A dedicated issuer host asked for something other than /connect. */
  REDIRECT_TO_APP: 'redirect-to-app',
  APP: 'app'
} as const;
export type ConnectRouteModeType = (typeof ConnectRouteMode)[keyof typeof ConnectRouteMode];

export const getConnectRouteMode = ({
  hostname,
  pathname
}: Pick<Location, 'hostname' | 'pathname'>): ConnectRouteModeType => {
  if (pathname === CONNECT_PATH) return ConnectRouteMode.CONNECT;
  if (DEDICATED_ISSUER_HOSTS.includes(hostname)) return ConnectRouteMode.REDIRECT_TO_APP;
  return ConnectRouteMode.APP;
};

export const ConnectFailure = {
  ...ConnectError,
  EXPIRED: 'expired',
  UNKNOWN: 'unknown'
} as const;
export type ConnectFailureType = (typeof ConnectFailure)[keyof typeof ConnectFailure];

export interface ConnectQuery {
  step: ConnectStepType;
  /** Present for every step except ERROR. */
  tx?: string;
  failure?: ConnectFailureType;
  /** Support reference, shown only when it looks like a request ID. */
  ref?: string;
  /** Host of the redirect the front refused, for display as plain text. Never navigated to. */
  target?: string;
  /** Email to prefill on the sign-in step. */
  loginHint?: string;
}

// Opaque, random and URL-safe. A looser value is never sent to the backend.
const TX_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const REF_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;
const MAX_LOGIN_HINT = 320;

// The target comes from an untrusted client, so only its host is ever shown, and only when it
// parses as an http(s) URL. URL.host is ASCII (punycode), so look-alike and bidi characters
// can't dress it up.
const displayTarget = (raw: string): string | undefined => {
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.host || undefined
      : undefined;
  } catch {
    return undefined;
  }
};

// A hint only prefills the email field, which the user can change. Printable ASCII with no
// whitespace and a non-leading @; anything else is dropped.
const sanitizeLoginHint = (raw: string | null): string | undefined =>
  raw !== null && raw.length <= MAX_LOGIN_HINT && /^[\x21-\x7e]+$/.test(raw) && raw.indexOf('@') > 0
    ? raw
    : undefined;

const failureFor = (error: string): ConnectFailureType => {
  if (error === ConnectError.REDIRECT) return ConnectFailure.REDIRECT;
  if (error === ConnectError.SIGNIN_FAILED) return ConnectFailure.SIGNIN_FAILED;
  // Any other value maps to fixed copy: the raw value is never displayed.
  return ConnectFailure.UNKNOWN;
};

export const parseConnectQuery = (search: string): ConnectQuery => {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  const rawRef = params.get('ref');
  const ref = rawRef !== null && REF_PATTERN.test(rawRef) ? rawRef : undefined;

  if (error !== null) {
    const failure = failureFor(error);
    const rawTarget = params.get('target');
    return {
      step: ConnectStep.ERROR,
      failure,
      ref,
      target:
        failure === ConnectFailure.REDIRECT && rawTarget ? displayTarget(rawTarget) : undefined
    };
  }

  const tx = params.get('tx');
  if (tx === null || !TX_PATTERN.test(tx)) {
    return { step: ConnectStep.ERROR, failure: ConnectFailure.UNKNOWN, ref };
  }
  if (params.get('step') === ConnectStep.CONSENT) {
    return { step: ConnectStep.CONSENT, tx };
  }
  return { step: ConnectStep.EMAIL, tx, loginHint: sanitizeLoginHint(params.get('login_hint')) };
};

export const consentStepUrl = (tx: string): string =>
  `${CONNECT_PATH}?${new URLSearchParams({ tx, step: ConnectStep.CONSENT })}`;

const REQUEST_TIMEOUT_MS = 15_000;

interface ErrorBody {
  error?: string;
  message?: string;
}

const readJson = async <T extends object>(res: Response): Promise<Partial<T> & ErrorBody> => {
  try {
    const json: unknown = await res.json();
    return json !== null && typeof json === 'object' ? json : {};
  } catch {
    // A gateway or SPA-fallback page, not the backend's JSON.
    return {};
  }
};

const request = async <T extends object>(
  path: string,
  init: RequestInit
): Promise<{ status: number; json: Partial<T> & ErrorBody }> => {
  const res = await fetch(path, {
    credentials: 'same-origin',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    ...init
  });
  return { status: res.status, json: await readJson<T>(res) };
};

const postJson = <T extends object>(path: string, body: Record<string, unknown>) =>
  request<T>(path, {
    method: 'POST',
    headers: { ...OAUTH_POST_HEADERS },
    body: JSON.stringify(body)
  });

const isTxExpired = (status: number, json: ErrorBody): boolean =>
  status === 400 && json.error === 'tx_expired';

export const oauthRouteEmail = async (email: string): Promise<OAuthRouteResult> => {
  const { status, json } = await postJson<{ method?: string; provider?: string }>('/oauth/route', {
    email
  });
  if (status === 403) {
    throw new NotAuthorizedError('Sign-in is not available for this account.');
  }
  if (status === 200 && json.method === OAuthRouteMethod.FEDERATED) {
    return { method: OAuthRouteMethod.FEDERATED, provider: json.provider ?? '' };
  }
  if (status === 200 && json.method === OAuthRouteMethod.PASSWORD) {
    return { method: OAuthRouteMethod.PASSWORD };
  }
  throw new AuthServiceError(`oauth-client: unexpected /oauth/route response (status ${status})`);
};

/** Navigates to /oauth/login, which redirects to the identity provider. */
export const beginOAuthFederatedLogin = ({
  tx,
  provider,
  loginHint
}: {
  tx: string;
  provider: string;
  /** The email the user typed, passed on so the identity provider can prefill it. */
  loginHint?: string;
}): void => {
  const params = new URLSearchParams({ tx, provider });
  if (loginHint) {
    params.set('login_hint', loginHint);
  }
  window.location.assign(`/oauth/login?${params}`);
};

const signInResult = (status: number, json: { status?: string }): OAuthSignInResult | null => {
  if (status !== 200) return null;
  if (json.status === OAuthSignInStatus.SIGNED_IN) return { status: OAuthSignInStatus.SIGNED_IN };
  if (json.status === OAuthSignInStatus.NEW_PASSWORD_REQUIRED) {
    return { status: OAuthSignInStatus.NEW_PASSWORD_REQUIRED };
  }
  return null;
};

export const oauthPasswordSignIn = async (
  tx: string,
  email: string,
  password: string
): Promise<OAuthSignInResult> => {
  const { status, json } = await postJson<{ status?: string }>('/oauth/password', {
    tx,
    email,
    password
  });
  const result = signInResult(status, json);
  if (result) return result;
  if (isTxExpired(status, json)) throw new TxExpiredError();
  if (status === 401 && json.error === 'invalid_credentials') {
    throw new InvalidCredentialsError('Incorrect email or password.');
  }
  if (status === 429) {
    throw new TooManyAttemptsError(
      'Too many sign-in attempts. Please wait a moment and try again.'
    );
  }
  throw new AuthServiceError(
    `oauth-client: unexpected /oauth/password response (status ${status})`
  );
};

export const oauthCompleteNewPassword = async (tx: string, newPassword: string): Promise<void> => {
  const { status, json } = await postJson<{ status?: string }>('/oauth/new-password', {
    tx,
    newPassword
  });
  if (status === 200 && json.status === OAuthSignInStatus.SIGNED_IN) return;
  if (isTxExpired(status, json)) throw new TxExpiredError();
  if (status === 400 && json.error === 'invalid_password') {
    throw new InvalidPasswordError(json.message ?? 'That password does not meet the policy.');
  }
  if (status === 401 && json.error === 'challenge_expired') {
    throw new ChallengeExpiredError('That sign-in attempt expired. Please sign in again.');
  }
  if (status === 429) {
    throw new TooManyAttemptsError(
      'Too many sign-in attempts. Please wait a moment and try again.'
    );
  }
  throw new AuthServiceError(
    `oauth-client: unexpected /oauth/new-password response (status ${status})`
  );
};

const isConsentInfo = (json: Partial<ConsentInfo>): json is ConsentInfo =>
  typeof json.harness === 'string' &&
  typeof json.redirectHost === 'string' &&
  typeof json.email === 'string' &&
  typeof json.remembered === 'boolean';

export const getConsentInfo = async (tx: string): Promise<ConsentInfo> => {
  const { status, json } = await request<Partial<ConsentInfo>>(
    `/oauth/consent?${new URLSearchParams({ tx })}`,
    { method: 'GET' }
  );
  if (isTxExpired(status, json)) throw new TxExpiredError();
  if (status === 200 && isConsentInfo(json)) {
    return {
      harness: json.harness,
      redirectHost: json.redirectHost,
      email: json.email,
      remembered: json.remembered
    };
  }
  throw new AuthServiceError(`oauth-client: unexpected /oauth/consent response (status ${status})`);
};

// Registered harness redirects are https or loopback http. Anything else in the answer is a
// fault, never navigated to (a javascript: or data: URL would run in this origin).
const LOOPBACK_HOSTS: readonly string[] = ['localhost', '127.0.0.1'];
const isNavigableRedirect = (value: string): boolean => {
  try {
    const { protocol, hostname } = new URL(value);
    return protocol === 'https:' || (protocol === 'http:' && LOOPBACK_HOSTS.includes(hostname));
  } catch {
    return false;
  }
};

/** Records the decision and returns where to send the browser: the harness redirect with a code, or with access_denied. */
export const submitConsent = async (tx: string, allow: boolean): Promise<ConsentResult> => {
  const { status, json } = await postJson<Partial<ConsentResult>>('/oauth/consent', {
    tx,
    allow
  });
  if (isTxExpired(status, json)) throw new TxExpiredError();
  if (status === 200 && typeof json.redirect === 'string' && isNavigableRedirect(json.redirect)) {
    return { redirect: json.redirect };
  }
  throw new AuthServiceError(`oauth-client: unexpected /oauth/consent response (status ${status})`);
};
